'use client'
import {grid, gridItem} from '~/design-system/foundations'
import {HgAspectRatio, HgHeading} from '~/design-system/hg/components'
import {getHeadingContentTypeProps} from '../../utils'
import {useEffect} from 'react'
import _ from 'lodash'
import {splitImageVariants, type SplitImageInnerHeroProps} from './types'
import {type HgHeroBaseProps} from '../../types'
import {tv} from 'tailwind-variants'
import HgIcon from '~/design-system/hg/components/HgIcon'

const heroBulletStyles = tv({
  slots: {
    container: 'mt-s5 w-full',
    innerContainer: 'grid w-full grid-cols-2 gap-s2',
    itemContainer: 'flex items-start gap-s2',
  },
})

function HeroBullets({bullets}: {bullets: SplitImageInnerHeroProps['heroBullets']}) {
  const {container, innerContainer, itemContainer} = heroBulletStyles()

  return (
    <div className={container()}>
      <div className={innerContainer()}>
        {bullets &&
          bullets.length > 0 &&
          bullets.map((bullet, index) => (
            <div key={index} className={itemContainer()}>
              <HgIcon
                iconType={bullet.iconType}
                size="small"
                className="flex-shrink-0 text-icon-default md:hidden"
              />
              <HgIcon
                iconType={bullet.iconType}
                size="medium"
                className="hidden flex-shrink-0 text-icon-default md:block"
              />
              <span className="break-words text-text-default arcadia-heading-10">
                {bullet.text}
              </span>
            </div>
          ))}
      </div>
    </div>
  )
}

function HandleHgAspectRatio({
  assetFourFiveRatio,
  assetOneOneRatio,
  assetThreeTwoRatio,
  imageWrapper,
}: Pick<
  SplitImageInnerHeroProps,
  'assetOneOneRatio' | 'assetThreeTwoRatio' | 'assetFourFiveRatio'
> & {imageWrapper: string}) {
  const commonClassNames = {
    wrapper: imageWrapper,
    assetWrapper: 'object-position-top h-full',
  }

  const renderAspectRatio = (
    content: React.ReactNode,
    ratio: '1:1' | '3:2' | '4:5',
    additionalClasses = 'block'
  ) => (
    <HgAspectRatio
      topSpacing="s6"
      aspectRatio={ratio}
      classNames={{
        ...commonClassNames,
        wrapper: `${commonClassNames.wrapper} ${additionalClasses}`,
      }}
    >
      {content}
    </HgAspectRatio>
  )

  // Primary case: Responsive layout with both ratios
  if (assetOneOneRatio && assetThreeTwoRatio) {
    return (
      <>
        {renderAspectRatio(assetOneOneRatio, '1:1', 'block md:hidden xl:block')}
        {renderAspectRatio(assetThreeTwoRatio, '3:2', 'hidden md:block xl:hidden')}
      </>
    )
  }

  // Fallback cases
  if (assetOneOneRatio) {
    return renderAspectRatio(assetOneOneRatio, '1:1')
  }

  if (assetThreeTwoRatio) {
    return renderAspectRatio(assetThreeTwoRatio, '3:2')
  }

  // Legacy: ideally we wouldn't support this
  // but need it for PartnerPageHeroSection
  // https://linear.app/mercury/issue/CP-789/add-1132-image-ratio-to-partner-page-hero-section
  if (assetFourFiveRatio) {
    return renderAspectRatio(assetFourFiveRatio, '4:5')
  }

  return null
}

export function SplitImageInnerHero({
  headingContainerRef,
  containerRef,
  headline,
  subhead,
  breadcrumbs,
  classNames,
  heroBullets,
  ...props
}: SplitImageInnerHeroProps & HgHeroBaseProps) {
  useEffect(() => {
    if (!containerRef.current) return

    function calculateTop() {
      if (!containerRef.current || !headingContainerRef.current) return

      if (window.innerHeight < containerRef.current.clientHeight) {
        headingContainerRef.current.setAttribute('data-sticky', 'true')
      } else {
        headingContainerRef.current.setAttribute('data-sticky', 'false')
      }

      const containerHeight = window.innerHeight
      const childHeight = headingContainerRef.current.clientHeight
      const topValue = (containerHeight - childHeight) / 2
      headingContainerRef.current.style.setProperty('--top', `${topValue}px`)
    }
    const debouncedCalculateTop = _.debounce(calculateTop, 100)
    debouncedCalculateTop()
    window.addEventListener('resize', debouncedCalculateTop)
    return () => {
      debouncedCalculateTop.cancel()
      window.removeEventListener('resize', debouncedCalculateTop)
    }
  }, [containerRef, headingContainerRef])

  const {imageWrapper, contentWrapper} = splitImageVariants({
    size: props.size,
    hasAdditionalContent: Boolean(props.additionalContent),
  })

  return (
    <div
      className={grid({
        className: classNames?.gridSlot,
      })}
    >
      <div
        className={gridItem({
          size: 'heading',
          className: contentWrapper({className: classNames?.headingContainer}),
        })}
        ref={headingContainerRef}
      >
        <div>
          {props.logoAsset && (
            <div className="mb-s4 w-fit max-w-full rounded-xl bg-surface-default p-s3 text-text-default arcadia-body-1">
              {props.logoAsset}
            </div>
          )}
          <HgHeading
            {...getHeadingContentTypeProps({
              props,
              buttonVariant: 'tonal',
              alwaysStackedEmail: true,
            })}
            variation={'section'}
            topSpacing={'s0'}
            breadcrumbs={breadcrumbs}
            className={classNames?.heading}
            headline={headline}
            subhead={subhead}
            alignment={'left'}
            additionalContentBeforeBody={<HeroBullets bullets={heroBullets} />}
          />
        </div>
        {props.additionalContent}
      </div>
      <HandleHgAspectRatio
        assetFourFiveRatio={props.assetFourFiveRatio}
        assetOneOneRatio={props.assetOneOneRatio}
        assetThreeTwoRatio={props.assetThreeTwoRatio}
        imageWrapper={imageWrapper()}
      />
    </div>
  )
}
